import React from "react";
import { Link } from "react-router-dom";
import posthog from "posthog-js";

import { isInAppPath } from "helpers/links";

import { resolveButtonState, type DisabledAppearance } from "./button-state";
import ButtonSpinner from "./ButtonSpinner";

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;

  /** "compact" is the call button's shape and fill (44px tall, no border) for
   * an action that stands beside it. "continue" is the quote flow's forward
   * button (2px border, peach fill). Both set their own padding, so `size`
   * does not apply to them. */
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "outline"
    | "text"
    | "subtle"
    | "compact"
    | "continue";
  size?: "xs" | "sm" | "md" | "lg";
  fullWidth?: boolean;

  href?: string;
  target?: "_blank" | "_self";
  rel?: string;

  className?: string;

  disabled?: boolean;
  /** Marks the button busy (`aria-busy`), disables it, and shows a spinner
   * before the label. A loading button is never faded. */
  isLoading?: boolean;
  /** Replaces the label while loading. The idle label keeps its room, so the
   * button does not change width when it swaps. */
  loadingText?: React.ReactNode;
  /** How the button appears when disabled. "faded" (default) reduces opacity, "solid" keeps full appearance */
  disabledAppearance?: DisabledAppearance;

  trackingId?: string;
  trackingEvent?: string;
  trackingProps?: Record<string, unknown>;

  ariaLabel?: string;
  ariaDescribedBy?: string;

  type?: "button" | "submit" | "reset";
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

function ButtonBody({
  children,
  icon,
  iconPosition,
  isLoading,
  loadingText,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  iconPosition: "left" | "right";
  isLoading: boolean;
  loadingText: React.ReactNode;
}) {
  const idle = (
    <>
      {iconPosition === "left" && icon}
      {children}
      {iconPosition === "right" && icon}
    </>
  );
  if (!isLoading) return idle;

  const working = (
    <>
      <ButtonSpinner />
      {loadingText ?? children}
    </>
  );
  if (loadingText === undefined) return working;

  return (
    <span className="inline-grid justify-items-center">
      <span
        aria-hidden="true"
        className="invisible col-start-1 row-start-1 flex items-center gap-2"
      >
        {idle}
      </span>
      <span className="col-start-1 row-start-1 flex items-center justify-center gap-2">
        {working}
      </span>
    </span>
  );
}

export default function Button({
  children,
  onClick,
  variant = "primary",
  size = "md",
  fullWidth = false,
  href,
  target,
  rel = target === "_blank" ? "noopener noreferrer" : undefined,
  className = "",
  disabled = false,
  isLoading = false,
  loadingText,
  disabledAppearance = "faded",
  trackingId,
  trackingEvent = "button_click",
  trackingProps = {},
  ariaLabel,
  ariaDescribedBy,
  type = "button",
  icon,
  iconPosition = "left",
}: ButtonProps) {
  const { isDisabled, ariaBusy, interactionClass } = resolveButtonState({
    disabled,
    isLoading,
    disabledAppearance,
  });
  const inactive = disabled || isLoading;

  const baseClasses = `rounded-lg transition-all items-center justify-center gap-2 ${
    variant === "continue"
      ? "inline-flex"
      : "flex text-center whitespace-nowrap"
  }`;

  const sizeClasses = {
    xs: "px-2 py-1 text-xs",
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-10 py-4 text-xl",
  };

  const variantClasses = {
    primary: inactive
      ? "font-bold text-soil bg-cantelope border-2 border-cantelope"
      : "font-bold text-soil hover:bg-soil bg-cantelope hover:text-cantelope hover:shadow-2xl border-2 border-cantelope hover:border-soil",
    secondary: inactive
      ? "font-bold text-oat border-soil bg-soil border-2"
      : "font-bold text-oat border-soil bg-soil border-2 hover:border-cantelope hover:bg-cantelope hover:text-soil",
    tertiary: inactive
      ? "font-bold text-soil bg-oat border border-oat"
      : "font-bold text-soil bg-oat border border-oat hover:bg-soil hover:text-oat",
    outline: inactive
      ? "font-bold text-soil border-soil bg-none border"
      : "font-bold text-soil border-soil bg-none border hover:bg-soil hover:text-oat",
    text: inactive
      ? "font-bold text-evergreen underline"
      : "font-bold text-evergreen hover:text-plum underline",
    subtle: inactive
      ? "font-medium text-soil border border-gray-300 bg-transparent"
      : "font-medium text-soil border border-gray-300 bg-transparent hover:bg-gray-100",
    continue: inactive
      ? "border-2 border-cantelope bg-cantelope px-[26px] py-2.5 font-bold text-soil"
      : "border-2 border-cantelope bg-cantelope px-[26px] py-2.5 font-bold text-soil hover:border-soil hover:bg-soil hover:text-cantelope focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope",
    compact: inactive
      ? "min-h-[44px] px-6 py-2 text-base font-bold text-soil bg-cantelope"
      : "min-h-[44px] px-6 py-2 text-base font-bold text-soil bg-cantelope hover:bg-cantelope/90",
  };

  const hasWidthClass = className.match(/\bw-\S+/);
  const widthClass = !hasWidthClass && (fullWidth ? "w-full" : "w-fit");

  const buttonClasses = `${baseClasses} ${variant === "compact" || variant === "continue" ? "" : sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${interactionClass} ${className}`;

  const iconJSX = icon ? (
    <span className="inline-flex items-center">{icon}</span>
  ) : null;

  const body = (
    <ButtonBody
      icon={iconJSX}
      iconPosition={iconPosition}
      isLoading={isLoading}
      loadingText={loadingText}
    >
      {children}
    </ButtonBody>
  );

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    if (trackingId) {
      posthog.capture(trackingEvent, {
        id: trackingId,
        ...trackingProps,
      });
    }

    if (onClick) {
      onClick(e);
    }
  };

  if (href) {
    const linkProps = {
      className: buttonClasses,
      onClick: handleClick,
      "aria-busy": ariaBusy,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      "data-ph-event-capture": trackingId ? "true" : undefined,
      "data-ph-event-name": trackingId ? trackingEvent : undefined,
    };
    if (!isDisabled && isInAppPath(href, target)) {
      return (
        <Link to={href} {...linkProps}>
          {body}
        </Link>
      );
    }
    return (
      <a
        href={isDisabled ? undefined : href}
        target={target}
        rel={rel}
        {...linkProps}
      >
        {body}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={isDisabled}
      aria-busy={ariaBusy}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      data-ph-event-capture={trackingId ? "true" : undefined}
      data-ph-event-name={trackingId ? trackingEvent : undefined}
    >
      {body}
    </button>
  );
}

export type { ButtonProps };
